import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useErrorContext } from '@qpr/client-library';
import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import AppBarContext from '../components/BusinessPortalAppBarContext';
import { getNavigationTarget, parseLocationObject } from '../utils/navigation-utils';
import { isMongoId } from '../utils/url-utils';

const PREFIX = 'GroupedKpiListPage';

const classes = {
  card: `${PREFIX}-card`,
  twoLineBox: `${PREFIX}-twoLineBox`,
  pageHeader: `${PREFIX}-pageHeader`,
  indicator: `${PREFIX}-indicator`,
  indicatorText: `${PREFIX}-indicatorText`,
  flexContainer: `${PREFIX}-flexContainer`,
  alignBottom: `${PREFIX}-alignBottom`
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.card}`]: {
    height: '120px',
    padding: theme.spacing(2),
    color: theme.palette.text.primary,
    '&:hover': {
      color: theme.palette.secondary.main,
      boxShadow:
        '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)'
    }
  },

  [`& .${classes.twoLineBox}`]: {
    display: '-webkit-box',
    boxOrient: 'vertical',
    lineClamp: 2,
    overflow: 'hidden'
  },

  [`& .${classes.pageHeader}`]: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4)
  },

  [`& .${classes.indicator}`]: {
    height: theme.spacing(1),
    width: theme.spacing(1),
    marginRight: theme.spacing(1),
    borderRadius: '50%',
    display: 'inline-block'
  },

  [`& .${classes.indicatorText}`]: {
    color: theme.palette.text.secondary
  },

  [`& .${classes.flexContainer}`]: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  },
  [`& .${classes.alignBottom}`]: { marginTop: 'auto' }
}));

interface KpiPageParams {
  tenantSymbol: string;
  meaId: string;
}

interface Data {
  id: string;
  name: string;
  typename: string;
  rangecolor: string;
  prettyrangevalue: string;
  prettyvalue: string;
  parentId: string;
  description: string;
  children: Data[];
}

const GroupedKpiListPage = (): JSX.Element => {
  const { params } = useRouteMatch<KpiPageParams>();
  const { search } = useLocation();
  const [viewdefinition, setViewDefinition] = useState({});
  const { setError } = useErrorContext();
  const { setAppBar, setPeriodSelector, selectedPeriod, getPeriodName } = useContext(AppBarContext);

  useEffect(() => {
    setAppBar(true);
    setPeriodSelector(true);
  }, []);

  const queryString = new URLSearchParams(search);
  const viewdefinitionId = queryString.get('viewdefinitionId');

  const { isLoading, error, data } = useQuery<AxiosResponse<Data[]>, AxiosError<ErrorObject>>(
    `kpi-list-${params.meaId}-${getPeriodName()}-${viewdefinitionId}`,
    async () => {
      const response = await axios.post('measurehierarchy', {
        parentId: params.meaId,
        recursive: true,
        attributes: `name, typename, rangecolor(period=${getPeriodName()}), prettyvalue(period=${getPeriodName()}), description, value(period=${getPeriodName()}), rangevalue(period=${getPeriodName()})`,
        sortby: 'level,order,name'
      });

      if (viewdefinitionId && isMongoId(viewdefinitionId)) {
        const viewResponse = await axios.get(`/viewdefinitions/${viewdefinitionId}`);
        if (viewResponse.data.data) setViewDefinition(viewResponse.data.data);
      }

      return response.data;
    },
    {
      // The query will not execute until the selectedPeriod exists
      enabled: !!selectedPeriod
    }
  );

  const noCardData = (): JSX.Element => (
    <Grid item>
      <Typography className="customHeader">No data to display</Typography>
    </Grid>
  );

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const getTarget = (measure: any, viewdefinition: any): string => {
    const defaultTarget = `/dashboard/${search ? `${search}&` : '?'}meaId=${measure.id}`;
    let target = getNavigationTarget(measure, viewdefinition) || defaultTarget;
    if (!target.includes('period=')) target += `&period=${getPeriodName()}`;
    return target;
  };

  const getSubMeasures = (subMeasure: Data): JSX.Element => (
    <Grid item xs={12} key={subMeasure.id} data-testid="kpilist-page-card">
      <Link
        to={parseLocationObject(
          `/${params.tenantSymbol}${getTarget(subMeasure, viewdefinition)}`,
          data?.data && Array.isArray(data.data) ? data.data[0].name || null : null
        )}
        style={{ textDecoration: 'none' }}
      >
        <Card className={classes.card}>
          <div className={classes.flexContainer}>
            <div>
              <div className={classes.twoLineBox}>
                <Typography color="inherit" data-testid="kpilist-page-card-title">
                  {subMeasure.name}
                </Typography>
              </div>
            </div>
            <div className={classes.alignBottom}>
              <Grid container alignItems="center">
                <Grid item>
                  <span
                    className={classes.indicator}
                    style={{ backgroundColor: `#${subMeasure.rangecolor}` }}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    variant="h4"
                    className={classes.indicatorText}
                    data-testid="kpilist-page-card-measurevalue"
                  >
                    {subMeasure.prettyvalue}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </div>
        </Card>
      </Link>
    </Grid>
  );

  const getCard = (measure: any): JSX.Element => (
    <Grid item xs={12} sm={6} md={3} key={measure.id}>
      <Grid container spacing={2} direction="column" data-testid="grouped-kpilist-page-column">
        <Grid item xs={12}>
          <div style={{ height: '100%' }}>
            <Typography variant="h5" noWrap data-testid="grouped-kpilist-page-column-title">
              {measure.name}
            </Typography>
          </div>
        </Grid>
        {measure.children.length > 0 ? measure.children.map(getSubMeasures) : noCardData()}
      </Grid>
    </Grid>
  );

  if (error && error.response) setError(error.response.data);

  if ((!data && !error) || isLoading)
    return (
      <div
        data-testid="grouped-kpilist-page-loading"
        style={{ textAlign: 'center', marginTop: '32px' }}
      >
        <CircularProgress />
      </div>
    );

  return (
    <StyledContainer data-testid="grouped-kpilist-page">
      <Typography
        className={`${classes.pageHeader} customHeader`}
        variant="h4"
        data-testid="grouped-kpilist-page-title"
      >
        {data?.data && Array.isArray(data.data) ? data.data[0].name || null : null}
      </Typography>
      <Grid container spacing={2}>
        {data?.data && Array.isArray(data.data) && data.data[0]?.children?.length > 0
          ? data.data[0].children.map(getCard)
          : noCardData()}
      </Grid>
    </StyledContainer>
  );
};

export default GroupedKpiListPage;
